<template>
  <div class="p-4">
    <h5 class="mb-4">XML Import Logs</h5>

    <div class="row mb-3">
      <div class="col-md-4">
        <label class="form-label">Client</label>
        <select class="form-control" v-model="selectedClient" @change="onClientChange">
          <option value="">All Clients</option>
          <option v-for="name in clientNames" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">Status</label>
        <select class="form-control" v-model="selectedStatus" @change="fetchLogs">
          <option value="">All</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="done">Done</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>
      <div class="col-md-5">
        <label class="form-label">Search</label>
        <input
          type="text"
          class="form-control"
          placeholder="Address, filename, or product ID"
          v-model="searchQuery"
          @input="onSearchInput"
        />
      </div>
    </div>

    <vue-good-table
      :columns="columns"
      :rows="rows"
      :sort-options="{enabled: false}"
      :pagination-options="paginationOptions"
      mode="remote"
      @on-page-change="handlePageChanged"
      :totalRows="totalRows"
      :isLoading.sync="api['GET-LOGS'].loading"
    >
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'status'">
          <span :class="statusClass(props.row.status)" class="badge">
            {{ props.row.status }}
          </span>
        </span>
        <span v-else-if="props.column.field === 'error_message'">
          <span v-if="props.row.error_message" class="text-danger small">
            {{ props.row.error_message }}
          </span>
          <span v-else class="text-muted">—</span>
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] || '—' }}
        </span>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {VueGoodTable} from 'vue-good-table';
import ApiCallMixin from '../../mixins/ApiCallMixin';
import XmlImportLogServiceV2 from '../../services/V2/XmlImportLogServiceV2';
import moment from 'moment';

export default {
  mixins: [ApiCallMixin],
  components: {VueGoodTable},
  data() {
    return {
      rows: [],
      totalRows: 0,
      currentPage: 1,
      clientNames: [],
      selectedClient: '',
      selectedStatus: '',
      searchQuery: '',
      searchDebounce: null,
    };
  },
  computed: {
    columns() {
      return [
        {field: 'client_name', label: 'Client'},
        {field: 'product_id', label: 'Product ID'},
        {field: 'address', label: 'Address'},
        {field: 'filename', label: 'Filename'},
        {field: 'status', label: 'Status'},
        {field: 'queued_at', label: 'Queued', formatFn: this.formatDate},
        {field: 'started_at', label: 'Started', formatFn: this.formatDate},
        {field: 'completed_at', label: 'Completed', formatFn: this.formatDate},
        {field: 'error_message', label: 'Error'},
      ];
    },
    paginationOptions() {
      return {
        enabled: true,
        mode: 'pages',
        perPageDropdownEnabled: false,
        setCurrentPage: this.currentPage,
        perPage: 20,
      };
    },
  },
  methods: {
    async fetchClientNames() {
      const response = await this.request('GET-CLIENTS', XmlImportLogServiceV2.getClientNames);
      if (response && response.data) {
        this.clientNames = response.data.data;
      }
    },
    async fetchLogs(page = 1) {
      this.currentPage = page;
      const payload = {page, per_page: 20};
      if (this.selectedClient) payload.client_name = this.selectedClient;
      if (this.selectedStatus) payload.status = this.selectedStatus;
      if (this.searchQuery) payload.search = this.searchQuery;

      const response = await this.request('GET-LOGS', XmlImportLogServiceV2.getLogs, payload);
      if (response && response.data) {
        this.rows = response.data.data;
        this.totalRows = response.data.meta.total;
      }
    },
    onClientChange() {
      this.fetchLogs(1);
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.fetchLogs(1), 400);
    },
    handlePageChanged(params) {
      this.fetchLogs(params.currentPage);
    },
    formatDate(value) {
      if (!value) return '—';
      return moment(value).format('DD MMM YYYY HH:mm:ss');
    },
    statusClass(status) {
      const map = {
        done: 'badge-xml-done',
        failed: 'badge-xml-failed',
        processing: 'badge-xml-processing',
        queued: 'badge-xml-queued',
        skipped: 'badge-xml-skipped',
      };
      return map[status] || 'badge-xml-processing';
    },
  },
  created() {
    this.registerApis(['GET-LOGS', 'GET-CLIENTS']);
    this.fetchClientNames();
    this.fetchLogs();
  },
};
</script>
